import React from 'react';
import PropTypes from 'prop-types';
import Dropdown from 'react-bootstrap/Dropdown';
import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import styles from './SelectSearch.module.scss';

/**
 * Search field with a "search by" selector in front of it, e.g.
 * [Purchase ID ▾ | 🔍 Enter Purchase ID ×  (Search)].
 *
 * Fully controlled: the parent owns the selected option and the text value.
 */
const SelectSearch = ({
  options,
  selectedKey,
  onSelectChange,
  value,
  onChange,
  onSearch,
  onClear,
  maxLength,
  placeholder,
  selectLabel,
  disabled,
  showSearchButton,
  selectWidth,
}) => {
  const selected = options.find((option) => option.key === selectedKey) || options[0];
  const inputPlaceholder = placeholder || `Enter ${selected ? selected.label : ''}`;

  const handleSubmit = (event) => {
    event.preventDefault();
    if (value && value.trim()) {
      onSearch(value.trim());
    }
  };

  const handleSelect = (key) => {
    const option = options.find((item) => item.key === key);
    if (option && option.key !== (selected && selected.key)) {
      onSelectChange(option.key, option.label);
    }
  };

  return (
    <form className={styles.root} onSubmit={handleSubmit} noValidate role='search'>
      <div className={styles.field}>
        {/* Bootstrap dropdown (not a native <select>) so the option list
            uses the same grey highlight as every other dropdown */}
        <Dropdown className={styles.select_wrap} onSelect={handleSelect}>
          <Dropdown.Toggle
            className={styles.select}
            style={selectWidth ? { width: selectWidth, maxWidth: 'none' } : undefined}
            disabled={disabled}
            aria-label={`${selectLabel}: ${selected ? selected.label : ''}`}
          >
            <span className={styles.select_label}>{selected ? selected.label : ''}</span>
          </Dropdown.Toggle>
          <Dropdown.Menu className={styles.select_menu}>
            {options.map((option) => (
              <Dropdown.Item
                key={option.key}
                eventKey={option.key}
                active={selected ? option.key === selected.key : false}
              >
                {option.label}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>

        <input
          type='text'
          className={styles.input}
          placeholder={inputPlaceholder}
          aria-label={inputPlaceholder}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          maxLength={maxLength}
          disabled={disabled}
        />

        {value && (
          <button
            type='button'
            className={styles.clear_button}
            onClick={onClear}
            aria-label='Clear search'
            disabled={disabled}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        )}

        {showSearchButton && (
          <button
            type='submit'
            className={styles.search_button}
            aria-label='Search'
            title='Search'
            disabled={disabled || !value || !value.trim()}
          >
            <FontAwesomeIcon icon={faSearch} />
          </button>
        )}
      </div>
    </form>
  );
};

SelectSearch.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({ key: PropTypes.string.isRequired, label: PropTypes.string.isRequired })
  ).isRequired,
  selectedKey: PropTypes.string,
  onSelectChange: PropTypes.func.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onSearch: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  maxLength: PropTypes.number,
  placeholder: PropTypes.string,
  selectLabel: PropTypes.string,
  disabled: PropTypes.bool,
  // Hide the built-in search button when the page has its own Search action
  // (Enter still submits).
  showSearchButton: PropTypes.bool,
  // Fixed width for the "search by" selector, for long option labels.
  selectWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

SelectSearch.defaultProps = {
  selectedKey: undefined,
  value: '',
  maxLength: 50,
  placeholder: '',
  selectLabel: 'Search by',
  disabled: false,
  showSearchButton: true,
  selectWidth: undefined,
};

export default SelectSearch;
